import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['title', 'icon', 'list', 'rowTemplate', 'whitelistIconTemplate', 'blacklistIconTemplate'];
  static values = {
    scope: String,
    whitelistTitle: String,
    blacklistTitle: String,
  };

  connect() {
    this.scopeElement = this.element.closest(`[data-rule-scope="${this.scopeValue}"]`) || this.element.parentElement;
    this.boundUpdate = () => this.scheduleUpdate();
    this.scopeElement.addEventListener('change', this.boundUpdate);
    this.scopeElement.addEventListener('click', this.boundUpdate);
  }

  disconnect() {
    if (this.scopeElement) {
      this.scopeElement.removeEventListener('change', this.boundUpdate);
      this.scopeElement.removeEventListener('click', this.boundUpdate);
    }
    if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
  }

  scheduleUpdate() {
    if (this.pendingFrame) cancelAnimationFrame(this.pendingFrame);
    this.pendingFrame = requestAnimationFrame(() => {
      this.pendingFrame = null;
      this.update();
    });
  }

  update() {
    const direction = this.readDirection();

    if (!direction) {
      this.element.classList.add('hidden');
      return;
    }
    this.element.classList.remove('hidden');

    const isWhitelist = direction !== 'blacklist';

    if (this.hasTitleTarget) {
      this.titleTarget.textContent = isWhitelist ? this.whitelistTitleValue : this.blacklistTitleValue;
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.remove('text-olive-1000', 'text-rose-1000');
      this.iconTarget.classList.add(isWhitelist ? 'text-olive-1000' : 'text-rose-1000');
      this.swapIconSvg(isWhitelist);
    }

    if (!this.hasListTarget || !this.hasRowTemplateTarget) return;

    const colourClass = isWhitelist ? 'text-olive-1000' : 'text-rose-1000';
    const entries = this.collectSelectedEntries();

    this.listTarget.innerHTML = '';
    entries.forEach((entry) => {
      this.listTarget.appendChild(this.buildRow(entry, colourClass));
    });
  }

  swapIconSvg(isWhitelist) {
    const template = isWhitelist ? this.whitelistIconTemplateTarget : this.blacklistIconTemplateTarget;
    if (!template) return;
    this.iconTarget.innerHTML = '';
    this.iconTarget.appendChild(template.content.cloneNode(true));
  }

  buildRow(entry, colourClass) {
    const fragment = this.rowTemplateTarget.content.cloneNode(true);
    const row = fragment.querySelector('[data-value], div');
    row.dataset.value = entry.value;

    const label = row.querySelector('[data-role="label"]');
    label.textContent = entry.label;
    label.classList.add(colourClass);

    const removeBtn = row.querySelector('[data-role="remove"]');
    removeBtn.dataset.value = entry.value;

    return row;
  }

  removeItem(event) {
    event.preventDefault();
    const value = event.currentTarget.dataset.value;
    if (!value) return;

    const checkbox = this.scopeElement.querySelector(
      `input[type="checkbox"][name$="[values][]"][value="${CSS.escape(value)}"]`,
    );
    if (!checkbox) return;

    checkbox.checked = false;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  readDirection() {
    const hidden = this.scopeElement.querySelector('input[type="hidden"][name$="[direction]"]');
    return hidden ? hidden.value : '';
  }

  collectSelectedEntries() {
    const checkboxes = this.scopeElement.querySelectorAll('input[type="checkbox"][name$="[values][]"]:checked');
    const seen = new Set();
    const entries = [];
    checkboxes.forEach((checkbox) => {
      const value = checkbox.value;
      if (!value || seen.has(value)) return;
      seen.add(value);
      const label = checkbox.dataset.label || checkbox.dataset.name || value;
      entries.push({ value, label });
    });
    return entries;
  }
}
